import { useEffect, useMemo, useState } from "react"

import {
  useReportsTransportInvalidation,
  useReportTransport,
  useSetReportVisibilityTransport,
} from "@/reports"
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  toast,
} from "@coval/ui"
import { Copy, Lock, LockOpen, Share } from "lucide-react"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"
import { MakePublicConfirmDialog } from "@/components/shared/alerts/MakePublicConfirmDialog"

interface MultiRunShareButtonProps {
  runIds: string[]
  reportId: string
}

export const MultiRunShareButton: React.FC<MultiRunShareButtonProps> = ({
  runIds,
  reportId,
}) => {
  const { trackEvent } = useAnalytics()
  // The runs the open confirmation was opened for. The parent passes a live
  // selection, so publishing the current one could expose runs the user never
  // saw named in the dialog.
  const [pendingRunIds, setPendingRunIds] = useState<string[] | null>(null)
  const runIdsKey = runIds.join(",")

  useEffect(() => {
    setPendingRunIds(null)
  }, [reportId, runIdsKey])

  const { data: report } = useReportTransport(reportId, {
    enabled: !!reportId,
  })

  const reportsInvalidation = useReportsTransportInvalidation()
  const visibilityMutation = useSetReportVisibilityTransport({
    onSuccess: () => {
      void reportsInvalidation.invalidateReport(reportId)
      void reportsInvalidation.invalidateList()
    },
  })

  const isShared = useMemo(() => {
    return report?.permissions === "PUBLIC"
  }, [report?.permissions])

  const sharedPath = `/shared/reports/${reportId}`

  const handlePublish = async (runIdsToPublish: string[]) => {
    if (runIdsToPublish.length === 0 || visibilityMutation.isPending) {
      return false
    }
    try {
      await visibilityMutation.mutateAsync({
        report_id: reportId,
        run_ids: runIdsToPublish,
        is_public: true,
      })
      trackEvent(TRACKING_EVENTS.REPORTS.PUBLISHED, {
        report_id: reportId,
        run_count: runIdsToPublish.length,
      })
      return true
    } catch {
      toast.danger({
        title: "Error",
        description: "Failed to publish report.",
      })
      return false
    }
  }

  const handleConfirmPublish = async () => {
    if (!pendingRunIds) {
      return
    }
    // A failed publish keeps the confirmation open so the retry stays in place.
    if (await handlePublish(pendingRunIds)) {
      setPendingRunIds(null)
    }
  }

  const handleUnpublish = async () => {
    if (runIds.length === 0 || visibilityMutation.isPending) {
      return
    }
    try {
      await visibilityMutation.mutateAsync({
        report_id: reportId,
        run_ids: runIds,
        is_public: false,
      })
    } catch {
      toast.danger({
        title: "Error",
        description: "Failed to unpublish report.",
      })
    }
  }

  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="secondary" aria-label="Share report">
            <Share className="h-4 w-4" />
            Share
          </Button>
        </PopoverTrigger>
        <PopoverContent side="bottom" className="p-4 gap-4 w-96">
          <div className="flex flex-col w-full gap-4">
            <div>
              <div className="text-sm font-medium mb-1">Share publicly</div>
              <div className="text-xs text-coval-text-muted">
                {isShared
                  ? `All ${runIds.length} run${runIds.length !== 1 ? "s" : ""} are published. Anyone can access this link without a Coval account.`
                  : `Publish all ${runIds.length} run${runIds.length !== 1 ? "s" : ""} to generate a shareable link.`}
              </div>
            </div>

            {isShared && (
              <div className="flex items-center gap-2 justify-between">
                <a
                  href={sharedPath}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-coval-text-muted hover:text-coval-text transition-colors cursor-pointer break-all"
                >
                  {sharedPath}
                </a>
                <Button
                  variant="secondary"
                  icon
                  onClick={() => {
                    const fullUrl = window.location.origin + sharedPath
                    void navigator.clipboard.writeText(fullUrl)
                    trackEvent(TRACKING_EVENTS.REPORTS.SHARE_LINK_COPIED, {
                      report_id: reportId,
                    })
                  }}
                  tooltip="Copy link to clipboard"
                >
                  <Copy size={14} />
                </Button>
              </div>
            )}

            {isShared ? (
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  disabled={visibilityMutation.isPending}
                  onClick={() => {
                    void handleUnpublish()
                  }}
                  className="flex-1"
                >
                  <LockOpen size={14} />
                  {visibilityMutation.isPending
                    ? "Unpublishing…"
                    : "Unpublish all"}
                </Button>
                <Button variant="secondary" asChild className="flex-1">
                  <a
                    href={sharedPath}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Share size={14} />
                    View shared page
                  </a>
                </Button>
              </div>
            ) : (
              <Button
                variant="secondary"
                disabled={visibilityMutation.isPending || runIds.length === 0}
                onClick={() => setPendingRunIds(runIds)}
              >
                <Lock size={14} />
                {visibilityMutation.isPending
                  ? "Publishing…"
                  : "Publish shareable link"}
              </Button>
            )}
          </div>
        </PopoverContent>
      </Popover>
      <MakePublicConfirmDialog
        open={pendingRunIds !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPendingRunIds(null)
          }
        }}
        onConfirm={() => {
          void handleConfirmPublish()
        }}
        resource="report"
        detail={`All ${pendingRunIds?.length ?? 0} run${pendingRunIds?.length !== 1 ? "s" : ""} in this report will be published.`}
        pending={visibilityMutation.isPending}
      />
    </>
  )
}
